import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaStar, FaRegStar, FaTrash, FaShare } from 'react-icons/fa';
import { Helmet } from 'react-helmet';

interface Movie {
  id: string;
  filename: string;
  upload_date: string;
  recorded_date: string | null;
  s3_link: string;
  still_link: string;
  tags: string[];
  favorite: boolean;
}

function MovieDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [movie, setMovie] = useState<Movie | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Parse URL parameters for quick loading
  const params = new URLSearchParams(location.search);
  const quickLoadStillLink = params.get('still');
  const quickLoadVideoLink = params.get('video');

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    fetch(`/api/movies/${id}`)
      .then((response) => response.json())
      .then((data: Movie) => {
        setMovie(data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching movie:', error);
        setIsLoading(false);
      });
  }, [id]);

  useEffect(() => {
    if (movie) {
      const ogUrlMeta = document.querySelector('meta[property="og:url"]');
      if (ogUrlMeta) {
        ogUrlMeta.setAttribute('content', `https://trailcam.tref.fun/movie/${movie.id}`);
      } else {
        const newMeta = document.createElement('meta');
        newMeta.setAttribute('property', 'og:url');
        newMeta.setAttribute('content', `https://trailcam.tref.fun/movie/${movie.id}`);
        document.head.appendChild(newMeta);
      }
    }
  }, [movie]);

  const handleBack = () => {
    if (location.state && location.state.from) {
      navigate(location.state.from);
    } else {
      navigate(`/${location.search}`);
    }
  };

  // Function to toggle favorite status
  const toggleFavorite = () => {
    if (!movie) return;

    fetch(`/api/movies/${movie.id}/favorite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ favorite: !movie.favorite }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to update favorite status');
        }
        return response.json();
      })
      .then(() => {
        // Update the movie's favorite status in state
        setMovie({ ...movie, favorite: !movie.favorite });
      })
      .catch((error) => {
        console.error('Error updating favorite status:', error);
      });
  };

  // Function to handle sharing
  const handleShare = () => {
    if (!movie) return;
    // const videoId = movie.s3_link.split('/').pop()?.split('_')[0];
    const shareUrl = `${window.location.origin}/movie/${movie.id}?still=${encodeURIComponent(movie.still_link)}&video=${encodeURIComponent(movie.s3_link)}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      alert('Link copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy link: ', err);
    });
  };

  // Function to delete the movie
  const handleDelete = () => {
    if (!movie) return;

    if (!window.confirm('Are you sure you want to delete this movie?')) {
      return;
    }

    fetch(`/api/movies/${movie.id}`, {
      method: 'DELETE',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to delete movie');
        }
        return response.json();
      })
      .then(() => {
        // {{ Edit 1: Navigate back to the same page after deletion }}
        handleBack();
      })
      .catch((error) => {
        console.error('Error deleting movie:', error);
      });
  };

  const renderContent = () => {
    if (isLoading && !quickLoadStillLink && !quickLoadVideoLink) {
      return (
        <div className="container mx-auto p-4 flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
        </div>
      );
    }

    const title = movie ? `Trailcam - ${formatTimestamp(movie.recorded_date || movie.upload_date)}` : 'Trailcam';
    const description = movie ? `Trailcam footage from ${formatTimestamp(movie.recorded_date || movie.upload_date)}` : 'Trailcam footage';
    const imageUrl = quickLoadStillLink || (movie && movie.still_link) || '';
    const videoUrl = quickLoadVideoLink || (movie && movie.s3_link) || '';

    return (
      <div className="container mx-auto p-4">
        <Helmet>
          <title>{title}</title>
          <meta name="description" content={description} />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta property="og:image" content={imageUrl} />
          <meta property="og:type" content="video.other" />
          <meta property="og:url" content={window.location.href} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={description} />
          <meta name="twitter:image" content={imageUrl} />
        </Helmet>

        <button onClick={handleBack} className="mb-4 inline-block text-blue-500 hover:text-blue-700">
          ← Back to list
        </button>
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {videoUrl ? (
            <video controls className="w-full">
              <source src={videoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          ) : (
            <div className="w-full h-64 bg-gray-200 flex items-center justify-center">
              <p>Loading video...</p>
            </div>
          )}
          <div className="p-4">
            {movie ? (
              <>
                <h2 className="text-lg font-semibold mb-1">
                  {formatTimestamp(movie.recorded_date || movie.upload_date)}
                </h2>
                <p className="text-sm text-gray-500 mb-1">Filename: {movie.filename}</p>
                <p className="text-sm text-gray-500 mb-2">
                  Uploaded: {formatTimestamp(movie.upload_date)}
                </p>
                <div className="flex flex-wrap gap-1 mb-2">
                  {movie.tags.map((tag, index) => (
                    <span
                      key={`${movie.id}-${tag}-${index}`}
                      className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                {/* Favorite, Share, and Delete Buttons */}
                <div className="p-4 flex items-center">
                  <button onClick={toggleFavorite} className="focus:outline-none mr-4" aria-label={movie.favorite ? 'Unfavorite' : 'Favorite'}>
                    {movie.favorite ? <FaStar className="text-yellow-500 w-6 h-6" /> : <FaRegStar className="text-gray-400 w-6 h-6" />}
                  </button>
                  <span className="mr-4 text-sm text-gray-600">{movie.favorite ? 'Favorited' : 'Mark as Favorite'}</span>
                  <button onClick={handleShare} className="focus:outline-none mr-4" aria-label="Share">
                    <FaShare className="text-blue-500 w-6 h-6" />
                  </button>
                  <span className="mr-4 text-sm text-gray-600">Share</span>
                  <button onClick={handleDelete} className="text-red-500 focus:outline-none mr-4" aria-label="Delete">
                    <FaTrash className="w-6 h-6" />
                  </button>
                  <span className="text-sm text-gray-600">Delete</span>
                </div>
              </>
            ) : (
              <p>Loading movie details...</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return renderContent();
}

function formatTimestamp(date: string | null): string {
  if (!date) return 'Unknown';
  return new Date(date).toLocaleString();
}

export default MovieDetail;
